import { useEffect } from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { HorizontalLocalMenu, Tab } from '@praktio/common-ui';

import Report from '../../common/Report/Report';
import PageHeader from '../../common/PageHeader/PageHeader';
import TableSummaryReport from './TableSummaryReport/TableSummaryReport';
import ReportSettingsPane from './ReportSettingsPane/ReportSettingsPane';
import LoadingErrorBoundaryRTK from '../../ui/LoadingErrorBoundaryRTK/LoadingErrorBoundaryRTK';

import { actions } from '../../../ducks/report';
import { getTeams } from '../../../ducks/teams';
import { RootState } from '../../../ducks/store';
import { useGetAdminReportQuery } from '../../../services/reports';
import { useGetTeamsSlice } from '../../../hooks/useGetTeamsSlice';
import { useFetchCoursesOnLoad } from '../../../hooks/useFetchCoursesOnLoad';

import './GeneralReport.scss';

const GeneralReport = () => {
  // ===========================================================================
  // Redux
  // ===========================================================================

  const dispatch = useDispatch();

  const { activeTeams, activeDeselectedCourses, reportSettingsPaneOpen, teams, user } = useSelector(
    (state: RootState) => ({
      activeTeams: state.teamsSlice.activeTeams,
      activeDeselectedCourses: state.teamsSlice.activeDeselectedCourses,
      reportSettingsPaneOpen: state.report.reportSettingsPaneOpen,
      teams: state.teams.list,
      user: state.auth.authenticatedUser,
    }),
  );

  // ===========================================================================
  // Hooks
  // ===========================================================================

  useFetchCoursesOnLoad();

  const { noCoursesSelected } = useGetTeamsSlice();

  // ===========================================================================
  // RTK Query
  // ===========================================================================

  const { data, isLoading, isFetching, error } = useGetAdminReportQuery(
    { activeTeams, activeDeselectedCourses },
    { skip: noCoursesSelected }, // Skip the query if there are no courses selected
  );

  // ===========================================================================
  // Other
  // ===========================================================================

  const dataIsLoading = isLoading || isFetching;

  // ===========================================================================
  // Use Effects
  // ===========================================================================

  useEffect(() => {
    dispatch(getTeams()); // Fetch teams on mount
  }, [dispatch]);

  useEffect(() => {
    if (noCoursesSelected) {
      dispatch({ type: actions.CLEAR_REPORT_DATA }); // Clear the report data if there are no active teams or courses
      return;
    }

    dispatch({ type: actions.GET_REPORT_DATA, payload: data }); // Get report data
  }, [noCoursesSelected, data, dispatch]);

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <div className="GeneralReport d-flex flex-column">
      <Helmet>
        <title>Reports - Praktio</title>
      </Helmet>

      <PageHeader pageName="Reports" className="mb-0" />

      <div className="GeneralReport_MainContainer">
        <ReportSettingsPane teams={teams} />

        <HorizontalLocalMenu
          classes={classNames({
            'HorizontalLocalMenu--open': reportSettingsPaneOpen,
          })}
          defaultActiveKey="summaryView"
        >
          <Tab eventKey="summaryView" title="Summary View">
            <LoadingErrorBoundaryRTK isLoading={dataIsLoading} error={error as FetchBaseQueryError}>
              <TableSummaryReport reportData={data} />
            </LoadingErrorBoundaryRTK>
          </Tab>

          <Tab eventKey="detailView" title="Detail View">
            <LoadingErrorBoundaryRTK isLoading={dataIsLoading} error={error as FetchBaseQueryError}>
              <Report user={user} reportData={data} isGeneral />
            </LoadingErrorBoundaryRTK>
          </Tab>
        </HorizontalLocalMenu>
      </div>
    </div>
  );
};

export default GeneralReport;
